/* Shared quiz logic. Health answers are deliberately kept in browser memory. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.PremeGutQuizCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function scoreQuiz(data, answers) {
    const scores = data.outcomes.map(() => 0);
    let answered = 0;
    for (const question of data.questions) {
      const requested = new Set(answers[question.id] || []);
      const selected = question.choices.filter((choice) => requested.has(choice.id)).slice(0, question.max);
      if (selected.length) answered += 1;
      for (const choice of selected) {
        choice.weights.forEach((weight, index) => { scores[index] += weight; });
      }
    }
    const highest = Math.max(...scores);
    // Typeform resolves ties using the first ending in the original ending order.
    // An entirely unscored response gets neutral guidance instead of a condition.
    const outcome = highest > 0 ? data.outcomes[scores.indexOf(highest)] : null;
    return { outcome, scores, answered };
  }

  function normalizePhone(value) {
    const raw = value.trim();
    if (!/^[+\d\s().-]+$/.test(raw)) return null;
    let phone = raw.replace(/[\s().-]/g, '');
    if (phone.startsWith('00')) phone = '+' + phone.slice(2);
    if (/^\d{10}$/.test(phone)) phone = '+1' + phone;
    else if (/^1\d{10}$/.test(phone)) phone = '+' + phone;
    return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : null;
  }

  function profilePayload(contact, now) {
    return {
      data: {
        type: 'profile',
        attributes: {
          email: contact.email,
          phone_number: contact.phone,
          ...(contact.firstName ? { first_name: contact.firstName } : {}),
          properties: {
            preme_quiz_source: 'Native gut health quiz',
            preme_quiz_contact_captured_at: now,
            // A custom property also retains the submitted phone for existing
            // profiles whose identifier cannot be updated by a client endpoint.
            preme_quiz_phone_number: contact.phone
          }
        }
      }
    };
  }

  function subscriptionPayload(contact, listId) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Preme native gut health quiz — optional email signup',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: contact.email,
                // Explicit email-only consent: phone capture does not opt into SMS.
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
  }

  async function postKlaviyo(fetcher, endpoint, siteId, body, signal) {
    const response = await fetcher(`https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(siteId)}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: '2026-07-15'
      },
      body: JSON.stringify(body),
      signal
    });
    if (response.status !== 202) throw new Error('Contact submission was not accepted.');
  }

  async function captureContact(fetcher, config, contact, signal) {
    if (!config.siteId) throw new Error('Contact capture is not configured.');
    await postKlaviyo(fetcher, 'profiles', config.siteId,
      profilePayload(contact, new Date().toISOString()), signal);
    let emailSignupFailed = false;
    if (contact.emailConsent && config.listId) {
      try {
        await postKlaviyo(fetcher, 'subscriptions', config.siteId,
          subscriptionPayload(contact, config.listId), signal);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        // Contact capture succeeded. An optional newsletter failure must not
        // hold the results hostage or silently report that signup succeeded.
        emailSignupFailed = true;
      }
    }
    return { emailSignupFailed };
  }

  return { scoreQuiz, normalizePhone, profilePayload, subscriptionPayload, captureContact };
});
